import React from 'react';
import { Eye, Clock } from 'lucide-react';
import { SharedProjectCard } from '../../types';

interface ProjectCardProps {
  project: SharedProjectCard;
  onClick: (projectId: number) => void;
}

const formatViewCountRange = (count: number): string => {
  if (count === 0) return '0';
  if (count < 10) return '< 10';
  if (count < 50) return '10+';
  if (count < 100) return '50+';
  if (count < 500) return '100+';
  if (count < 1000) return '500+';
  if (count < 5000) return '1K+';
  if (count < 10000) return '5K+';
  return '10K+';
};

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onClick }) => {
  const handleClick = () => {
    onClick(project.project_id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(project.project_id);
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString();
    } catch {
      return '';
    }
  };

  // Determine which date to show based on event type
  const displayDate =
    project.event_type === 'new_version' && project.latest_version_published_at
      ? project.latest_version_published_at
      : project.updated_at;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="cursor-pointer bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 transition-all"
    >
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
          {project.name}
        </h3>

        {/* Event type badges */}
        {project.event_type === 'new_version' && project.latest_version_name && (
          <span
            aria-label={`New Version: ${project.latest_version_name}`}
            className="bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
          >
            New Version
          </span>
        )}
        {project.event_type === 'new_project' && (
          <span
            aria-label="New Project"
            className="bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
          >
            New Project
          </span>
        )}
      </div>

      {/* Version name display */}
      {project.event_type === 'new_version' && project.latest_version_name && (
        <p className="text-xs text-blue-600 dark:text-blue-400 mb-1 truncate">
          {project.latest_version_name}
        </p>
      )}

      {project.description && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2 overflow-hidden">
          {project.description}
        </p>
      )}

      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span className="text-primary-600 dark:text-primary-400 font-medium">
          @{project.owner_username}
        </span>

        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1" aria-label={`${formatViewCountRange(project.view_count)} views`}>
            <Eye className="w-3.5 h-3.5" aria-hidden="true" />
            {formatViewCountRange(project.view_count)}
          </span>

          {displayDate && (
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" aria-hidden="true" />
              {formatDate(displayDate)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
